import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';

const GDPR = () => {
  return (
    <>
      <SEO 
        title="GDPR Compliance - Data Protection & Privacy"
        description="Learn about Abacies Logiciels' commitment to GDPR compliance, data protection standards, and how we safeguard your personal information in accordance with EU regulations."
        keywords="GDPR compliance, data protection, privacy policy, EU regulations, personal data, data security, GDPR rights"
      />

      {/* Hero Section */}
      <section className="hero-section">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-charcoal animate-fade-in">
              GDPR <span className="text-gradient">Compliance</span>
            </h1>
            <p className="text-xl text-titanium leading-relaxed animate-slide-up">
              Our commitment to protecting your personal data and ensuring full compliance with the General Data Protection Regulation (GDPR)
            </p>
            <p className="text-sm text-cool-grey mt-4 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              Last Updated: January 2025
            </p>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-8 bg-white border-b border-platinum">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="#overview" className="px-4 py-2 bg-blush-light/40 text-primary-red rounded-lg hover:bg-blush-light transition-colors">
                Overview
              </a>
              <a href="#your-rights" className="px-4 py-2 bg-blush-light/40 text-primary-red rounded-lg hover:bg-blush-light transition-colors">
                Your Rights
              </a>
              <a href="#data-processing" className="px-4 py-2 bg-blush-light/40 text-primary-red rounded-lg hover:bg-blush-light transition-colors">
                Data Processing
              </a>
              <a href="#security" className="px-4 py-2 bg-blush-light/40 text-primary-red rounded-lg hover:bg-blush-light transition-colors">
                Security Measures
              </a>
              <a href="#contact" className="px-4 py-2 bg-blush-light/40 text-primary-red rounded-lg hover:bg-blush-light transition-colors">
                Contact DPO
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* GDPR Overview */}
      <section id="overview" className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-charcoal">What is GDPR?</h2>
            <p className="text-titanium mb-6">
              The General Data Protection Regulation (GDPR) is a comprehensive data protection law that came into effect on May 25, 2018, across the European Union. It sets strict guidelines for the collection, processing, and storage of personal data of EU residents, regardless of where the organization is located.
            </p>
            <p className="text-titanium mb-6">
              At Abacies Logiciels, we are fully committed to GDPR compliance and have implemented robust measures to ensure that your personal data is protected, processed lawfully, and used only for the purposes you have consented to.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="card text-center">
                <div className="w-16 h-16 bg-blush-light/40 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-charcoal">Data Protection</h3>
                <p className="text-sm text-titanium">Advanced security measures to safeguard your personal information</p>
              </div>
              <div className="card text-center">
                <div className="w-16 h-16 bg-blush-light/40 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-charcoal">Transparency</h3>
                <p className="text-sm text-titanium">Clear communication about how we use your data</p>
              </div>
              <div className="card text-center">
                <div className="w-16 h-16 bg-blush-light/40 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold mb-2 text-charcoal">Your Control</h3>
                <p className="text-sm text-titanium">Full rights to access, modify, and delete your data</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Your Rights Under GDPR */}
      <section id="your-rights" className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-charcoal">Your Rights Under GDPR</h2>
            <p className="text-titanium mb-8">
              As a data subject under GDPR, you have several fundamental rights regarding your personal data. We respect these rights and have established processes to ensure you can exercise them easily.
            </p>

            <div className="space-y-6">
              {/* Right to Access */}
              <div className="card">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                    <span className="text-xl font-bold text-primary-red">1</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3 text-charcoal">Right to Access</h3>
                    <p className="text-titanium mb-3">
                      You have the right to request access to your personal data and obtain a copy of the information we hold about you. We will provide this information in a structured, commonly used, and machine-readable format.
                    </p>
                    <p className="text-sm text-graphite">
                      <strong>How to exercise:</strong> Submit a data access request through our Data Privacy Portal or contact our Data Protection Officer.
                    </p>
                  </div>
                </div>
              </div>

              {/* Right to Rectification */}
              <div className="card">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                    <span className="text-xl font-bold text-primary-red">2</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3 text-charcoal">Right to Rectification</h3>
                    <p className="text-titanium mb-3">
                      If you believe any of your personal data we hold is inaccurate or incomplete, you have the right to have it corrected or completed without undue delay.
                    </p>
                    <p className="text-sm text-graphite">
                      <strong>How to exercise:</strong> Log into your account to update your information or contact our support team for assistance.
                    </p>
                  </div>
                </div>
              </div>

              {/* Right to Erasure */}
              <div className="card">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                    <span className="text-xl font-bold text-primary-red">3</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3 text-charcoal">Right to Erasure ("Right to be Forgotten")</h3>
                    <p className="text-titanium mb-3">
                      You can request the deletion of your personal data when it is no longer necessary for the purposes for which it was collected, or if you withdraw your consent and there is no other legal ground for processing.
                    </p>
                    <p className="text-sm text-graphite">
                      <strong>How to exercise:</strong> Submit a deletion request through our Data Privacy Portal. We will respond within 30 days.
                    </p>
                  </div>
                </div>
              </div>

              {/* Right to Restrict Processing */}
              <div className="card">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                    <span className="text-xl font-bold text-primary-red">4</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3 text-charcoal">Right to Restrict Processing</h3>
                    <p className="text-titanium mb-3">
                      In certain circumstances, you can request that we limit the processing of your personal data, such as when you contest the accuracy of the data or object to the processing.
                    </p>
                    <p className="text-sm text-graphite">
                      <strong>How to exercise:</strong> Contact our Data Protection Officer with details of your restriction request.
                    </p>
                  </div>
                </div>
              </div>

              {/* Right to Data Portability */}
              <div className="card">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                    <span className="text-xl font-bold text-primary-red">5</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3 text-charcoal">Right to Data Portability</h3>
                    <p className="text-titanium mb-3">
                      You have the right to receive your personal data in a structured, commonly used format and transmit that data to another controller without hindrance from us.
                    </p>
                    <p className="text-sm text-graphite">
                      <strong>How to exercise:</strong> Request a data export through our Data Privacy Portal in JSON, CSV, or XML format.
                    </p>
                  </div>
                </div>
              </div>

              {/* Right to Object */}
              <div className="card">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                    <span className="text-xl font-bold text-primary-red">6</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3 text-charcoal">Right to Object</h3>
                    <p className="text-titanium mb-3">
                      You have the right to object to the processing of your personal data for direct marketing purposes or when processing is based on legitimate interests or public interest.
                    </p>
                    <p className="text-sm text-graphite">
                      <strong>How to exercise:</strong> Opt-out of marketing communications via unsubscribe links or contact our Data Protection Officer for other objections.
                    </p>
                  </div>
                </div>
              </div>

              {/* Rights Related to Automated Decision-Making */}
              <div className="card">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-primary-red/10 rounded-lg flex items-center justify-center flex-shrink-0 mr-4">
                    <span className="text-xl font-bold text-primary-red">7</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold mb-3 text-charcoal">Rights Related to Automated Decision-Making</h3>
                    <p className="text-titanium mb-3">
                      You have the right not to be subject to decisions based solely on automated processing, including profiling, which produces legal effects or similarly significantly affects you.
                    </p>
                    <p className="text-sm text-graphite">
                      <strong>How to exercise:</strong> Request human review of any automated decisions by contacting our Data Protection Officer.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Data Processing */}
      <section id="data-processing" className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-charcoal">How We Process Your Data</h2>
            
            <div className="space-y-8">
              {/* Legal Basis */}
              <div>
                <h3 className="text-2xl font-semibold mb-4 text-charcoal">Legal Basis for Processing</h3>
                <p className="text-titanium mb-4">
                  We process your personal data only when we have a legal basis to do so. Under GDPR, the legal bases we rely on include:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="card bg-alabaster">
                    <h4 className="font-semibold text-charcoal mb-2">Consent</h4>
                    <p className="text-sm text-titanium">You have given clear consent for us to process your personal data for specific purposes.</p>
                  </div>
                  <div className="card bg-alabaster">
                    <h4 className="font-semibold text-charcoal mb-2">Contract Performance</h4>
                    <p className="text-sm text-titanium">Processing is necessary for a contract we have with you, or because you have asked us to take specific steps before entering into a contract.</p>
                  </div>
                  <div className="card bg-alabaster">
                    <h4 className="font-semibold text-charcoal mb-2">Legal Obligation</h4>
                    <p className="text-sm text-titanium">Processing is necessary for us to comply with the law (not including contractual obligations).</p>
                  </div>
                  <div className="card bg-alabaster">
                    <h4 className="font-semibold text-charcoal mb-2">Legitimate Interests</h4>
                    <p className="text-sm text-titanium">Processing is necessary for our legitimate interests or the legitimate interests of a third party, unless there is a good reason to protect your personal data which overrides those legitimate interests.</p>
                  </div>
                </div>
              </div>

              {/* Data We Collect */}
              <div>
                <h3 className="text-2xl font-semibold mb-4 text-charcoal">Types of Data We Collect</h3>
                <div className="card">
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <strong className="text-charcoal">Identity Data:</strong>
                        <span className="text-titanium"> Name, username, title, date of birth, and gender</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <strong className="text-charcoal">Contact Data:</strong>
                        <span className="text-titanium"> Email address, telephone numbers, billing address, and delivery address</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <strong className="text-charcoal">Technical Data:</strong>
                        <span className="text-titanium"> IP address, browser type and version, time zone setting, operating system and platform</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <strong className="text-charcoal">Usage Data:</strong>
                        <span className="text-titanium"> Information about how you use our website, products, and services</span>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <div>
                        <strong className="text-charcoal">Marketing Data:</strong>
                        <span className="text-titanium"> Your preferences for receiving marketing communications and your communication preferences</span>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Data Retention */}
              <div>
                <h3 className="text-2xl font-semibold mb-4 text-charcoal">Data Retention</h3>
                <p className="text-titanium mb-4">
                  We only retain your personal data for as long as necessary to fulfill the purposes we collected it for, including for the purposes of satisfying any legal, accounting, or reporting requirements.
                </p>
                <div className="card bg-blush-light/20 border-l-4 border-primary-red">
                  <p className="text-titanium">
                    <strong className="text-charcoal">Retention Period:</strong> Unless a longer retention period is required by law, we typically retain personal data for 7 years after the end of our business relationship with you. Marketing data may be retained for up to 3 years from your last interaction with us.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Security Measures */}
      <section id="security" className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-charcoal">Security Measures</h2>
            <p className="text-titanium mb-8">
              We have implemented appropriate technical and organizational measures to ensure a level of security appropriate to the risk, including protection against unauthorized or unlawful processing and against accidental loss, destruction, or damage.
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="card">
                <div className="flex items-start">
                  <svg className="w-8 h-8 text-primary-red mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-charcoal">Encryption</h3>
                    <p className="text-sm text-titanium">All data is encrypted in transit using TLS 1.3 and at rest using AES-256 encryption standards.</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-start">
                  <svg className="w-8 h-8 text-primary-red mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-charcoal">Access Controls</h3>
                    <p className="text-sm text-titanium">Strict access controls with role-based permissions and multi-factor authentication for all systems.</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-start">
                  <svg className="w-8 h-8 text-primary-red mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-charcoal">Regular Audits</h3>
                    <p className="text-sm text-titanium">Quarterly security audits and annual third-party penetration testing to identify vulnerabilities.</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-start">
                  <svg className="w-8 h-8 text-primary-red mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-charcoal">Staff Training</h3>
                    <p className="text-sm text-titanium">Mandatory GDPR and data protection training for all employees handling personal data.</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-start">
                  <svg className="w-8 h-8 text-primary-red mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-charcoal">Secure Infrastructure</h3>
                    <p className="text-sm text-titanium">ISO 27001 certified data centers with 24/7 monitoring and physical security.</p>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="flex items-start">
                  <svg className="w-8 h-8 text-primary-red mr-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-charcoal">Incident Response</h3>
                    <p className="text-sm text-titanium">24-hour breach notification process and comprehensive incident response procedures.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card bg-primary-red/5 border-l-4 border-primary-red mt-8">
              <h3 className="text-lg font-semibold mb-3 text-charcoal">Data Breach Notification</h3>
              <p className="text-titanium">
                In the event of a data breach that is likely to result in a high risk to your rights and freedoms, we will notify you and the relevant supervisory authority within 72 hours of becoming aware of the breach, as required by GDPR Article 33 and 34.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* International Data Transfers */}
      <section className="py-16 md:py-20 bg-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-charcoal">International Data Transfers</h2>
            <p className="text-titanium mb-6">
              We may transfer your personal data outside the European Economic Area (EEA) to countries that may not provide the same level of data protection as the EEA. When we do so, we ensure appropriate safeguards are in place:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="card">
                <h3 className="font-semibold text-charcoal mb-3">Standard Contractual Clauses</h3>
                <p className="text-sm text-titanium">We use European Commission-approved Standard Contractual Clauses (SCCs) for data transfers to third countries.</p>
              </div>
              <div className="card">
                <h3 className="font-semibold text-charcoal mb-3">Adequacy Decisions</h3>
                <p className="text-sm text-titanium">We transfer data only to countries that have been deemed to provide adequate protection by the European Commission.</p>
              </div>
              <div className="card">
                <h3 className="font-semibold text-charcoal mb-3">Privacy Shield (where applicable)</h3>
                <p className="text-sm text-titanium">We work with partners certified under appropriate data transfer frameworks when available.</p>
              </div>
              <div className="card">
                <h3 className="font-semibold text-charcoal mb-3">Binding Corporate Rules</h3>
                <p className="text-sm text-titanium">Our internal data transfer policies comply with GDPR requirements for international group transfers.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact DPO */}
      <section id="contact" className="py-16 md:py-20 bg-alabaster">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-charcoal">Contact Our Data Protection Officer</h2>
            <p className="text-titanium mb-8">
              If you have any questions about this GDPR compliance statement, wish to exercise your rights, or have concerns about how we handle your personal data, please contact our Data Protection Officer (DPO):
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="card">
                <h3 className="text-lg font-semibold mb-4 text-charcoal">Data Protection Officer</h3>
                <div className="space-y-3 text-titanium">
                  <p className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span><strong>Name:</strong> Sarah Mitchell, CIPP/E</span>
                  </p>
                  <p className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span><strong>Email:</strong> dpo@abacies.com</span>
                  </p>
                  <p className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span><strong>Phone:</strong> +1 (555) 123-GDPR</span>
                  </p>
                  <p className="flex items-start">
                    <svg className="w-5 h-5 text-primary-red mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span><strong>Address:</strong> 123 Technology Drive, Silicon Valley, CA 94025</span>
                  </p>
                </div>
              </div>

              <div className="card bg-blush-light/20">
                <h3 className="text-lg font-semibold mb-4 text-charcoal">Response Time</h3>
                <p className="text-titanium mb-4">
                  We aim to respond to all data protection inquiries and requests within:
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-primary-red rounded-full mr-3"></span>
                    <span className="text-titanium"><strong>General Inquiries:</strong> 5 business days</span>
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-primary-red rounded-full mr-3"></span>
                    <span className="text-titanium"><strong>Rights Requests:</strong> 30 calendar days</span>
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-primary-red rounded-full mr-3"></span>
                    <span className="text-titanium"><strong>Data Breaches:</strong> 72 hours (if applicable)</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="card bg-primary-red/5 border-l-4 border-primary-red">
              <h3 className="text-lg font-semibold mb-3 text-charcoal">Right to Lodge a Complaint</h3>
              <p className="text-titanium mb-3">
                You have the right to lodge a complaint with a supervisory authority if you believe we have not complied with GDPR. The lead supervisory authority for Abacies Logiciels is:
              </p>
              <p className="text-titanium">
                <strong>Irish Data Protection Commission</strong><br />
                21 Fitzwilliam Square South, Dublin 2, D02 RD28, Ireland<br />
                Website: <a href="https://www.dataprotection.ie" className="text-primary-red hover:underline" target="_blank" rel="noopener noreferrer">www.dataprotection.ie</a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related Policies */}
      <section className="py-16 bg-white">
        <div className="page-container">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-charcoal text-center">Related Policies & Information</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/legal/privacy" className="card hover:shadow-xl transition-all duration-300 text-center">
                <div className="w-12 h-12 bg-blush-light/40 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-charcoal mb-2">Privacy Policy</h3>
                <p className="text-sm text-titanium">Detailed privacy practices</p>
              </Link>
              <Link to="/legal/cookies" className="card hover:shadow-xl transition-all duration-300 text-center">
                <div className="w-12 h-12 bg-blush-light/40 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                  </svg>
                </div>
                <h3 className="font-semibold text-charcoal mb-2">Cookie Policy</h3>
                <p className="text-sm text-titanium">How we use cookies</p>
              </Link>
              <Link to="/legal/data-security" className="card hover:shadow-xl transition-all duration-300 text-center">
                <div className="w-12 h-12 bg-blush-light/40 rounded-full flex items-center justify-center mx-auto mb-3">
                  <svg className="w-6 h-6 text-primary-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-charcoal mb-2">Data Security</h3>
                <p className="text-sm text-titanium">Security measures in detail</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Updates & Changes */}
      <section className="py-12 bg-alabaster border-t border-platinum">
        <div className="page-container">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-xl font-semibold mb-4 text-charcoal">Updates to This Policy</h3>
            <p className="text-titanium mb-4">
              We may update this GDPR compliance statement from time to time to reflect changes in our practices or legal requirements. We will notify you of any material changes by posting the updated policy on our website and updating the "Last Updated" date.
            </p>
            <p className="text-sm text-graphite">
              For significant changes that affect your rights, we will provide prominent notice or seek your consent where required by law.
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default GDPR;